import React, {useCallback, useEffect, useRef, useState} from 'react';
import {findIndex, map} from 'lodash';
import Label from 'shared/components/label';
import AssetService from 'src/services/AssetService';
import {useField} from 'formik';
import placeholders from 'src/static/placeholders.json';
import FormikDropdown from 'src/components/formik-dropdown';
import FormikTextInput from '../formik-text-input';
import {AddressLineAutoComplete} from 'src/components/form/AddressLineTypeahead';

export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  subdivision: string;
  postalCode: number;
  country: string;
}

interface AddressInputLabels {
  addressLine1: string;
  addressLine2: string;
}

export interface AddressInputProps {
  formikFieldNames?: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    subdivision: string;
    postalCode: string;
    country: string;
  };
  labels?: AddressInputLabels;
  setAddressEUStatus?: (countryCode: string) => void;
  required?: boolean;
  requiredFields?: {
    addressLine1?: boolean;
    addressLine2?: boolean;
    city?: boolean;
    subdivision?: boolean;
    postalCode?: boolean;
    country?: boolean;
  };
  includeFields?: {
    addressLine1?: boolean;
    addressLine2?: boolean;
    city?: boolean;
    subdivision?: boolean;
    postalCode?: boolean;
    country?: boolean;
  };
}

const FormikAddressInput = ({
  formikFieldNames = {
    addressLine1: 'addressLine1',
    addressLine2: 'addressLine2',
    city: 'city',
    subdivision: 'subdivision',
    postalCode: 'postalCode',
    country: 'country',
  },
  labels = {addressLine1: 'Street Address 1', addressLine2: 'Street Address 2'},
  setAddressEUStatus,
  required = false,
  requiredFields = {
    addressLine1: true,
    addressLine2: false,
    city: true,
    subdivision: true,
    postalCode: true,
    country: true,
  },
  includeFields = {
    addressLine1: true,
    addressLine2: true,
    city: true,
    subdivision: true,
    postalCode: true,
    country: true,
  },
}: AddressInputProps) => {
  const [country] = useField<string>(formikFieldNames.country);
  const [addressLine2] = useField<string>(formikFieldNames.addressLine2);

  const [countries, setCountries] = useState<{_id: string; value: string; text: string}[]>([]);
  const [subdivisions, setSubdivisions] = useState<$TSFixMe[]>([]);
  const [subdivisionsLoading, setSubdivisionsLoading] = useState<boolean>(true);

  // Track previous country value to trigger updates when it changes.
  const prevCountryRef = useRef(country.value);

  const loadCountries = useCallback(async () => {
    try {
      const countriesList = await AssetService.getCountryList();
      const originalUsIndex = findIndex(countriesList, {code: 'US'});
      if (originalUsIndex >= 0) {
        // Move US object to the beginning of the array.
        const [us] = countriesList.splice(originalUsIndex, 1);
        countriesList.unshift(us);
      }
      const mapped = map(countriesList, (countryData) => ({
        _id: countryData.code,
        value: countryData.code,
        text: countryData.name,
      }));
      setCountries(mapped);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const refreshSubdivisions = useCallback(async (countryCode: string) => {
    setSubdivisionsLoading(true);
    const subs = await AssetService.getSubdivisionOptions(countryCode);
    setSubdivisions(subs);
    setSubdivisionsLoading(false);
  }, []);

  const updateEUStatus = useCallback(
    (countryCode: string) => {
      if (setAddressEUStatus) {
        setAddressEUStatus(countryCode);
      }
    },
    [setAddressEUStatus]
  );

  useEffect(() => {
    loadCountries();
    refreshSubdivisions(country.value);
  }, [loadCountries, refreshSubdivisions, country.value]);

  useEffect(() => {
    if (prevCountryRef.current !== country.value) {
      updateEUStatus(country.value);
      refreshSubdivisions(country.value);
      prevCountryRef.current = country.value;
    }
  }, [country.value, updateEUStatus, refreshSubdivisions]);

  const isDomestic = country.value === 'US';

  return (
    <div className="shared-address-input address-step">
      {includeFields.country && (
        <Label className="shared-address-input-line" required={requiredFields.country} noTopPadding>
          Country
          <FormikDropdown name={formikFieldNames.country} options={countries} />
        </Label>
      )}
      <Label
        className="shared-address-input-line"
        required={required && requiredFields.addressLine1}
      >
        {labels.addressLine1}
        <AddressLineAutoComplete
          fieldNames={{
            addressLine1: formikFieldNames.addressLine1,
            city: formikFieldNames.city,
            subdivision: formikFieldNames.subdivision,
            postalCode: formikFieldNames.postalCode,
            country: formikFieldNames.country,
          }}
        />
      </Label>
      {includeFields.addressLine2 && (
        <Label
          className="shared-address-input-line"
          required={required && requiredFields.addressLine2}
        >
          {labels.addressLine2}
          <FormikTextInput
            name={formikFieldNames.addressLine2}
            showCheck={!!addressLine2.value}
            type="text"
            placeholder={placeholders.address.line2}
            aria-label="Street Address, Apartment, Unit or Building Number"
            autoComplete="address-line2"
            required={required && requiredFields.addressLine2}
          />
        </Label>
      )}
      <div className="shared-address-input-city-state-zip shared-address-input-line">
        <Label required={required && requiredFields.city}>
          City
          <FormikTextInput
            name={formikFieldNames.city}
            required={required && requiredFields.city}
            showCheck
            type="text"
            placeholder={
              isDomestic ? placeholders.address.city : placeholders.address.internationalCity
            }
            aria-label="Street Address, City"
            autoComplete="locality"
          />
        </Label>
        {subdivisions.length > 0 && (
          <Label required={required && requiredFields.subdivision && isDomestic}>
            {isDomestic
              ? placeholders.address.domesticSubdivisionName
              : placeholders.address.internationalSubdivisionName}
            <FormikDropdown
              name={formikFieldNames.subdivision}
              className="subdivisions-select"
              disabled={subdivisionsLoading}
              options={subdivisions}
              autoComplete="region"
            />
          </Label>
        )}
        <Label required={required && requiredFields.postalCode && isDomestic}>
          {isDomestic
            ? placeholders.address.domesticPostalCodeName
            : placeholders.address.internationalPostalCodeName}
          <FormikTextInput
            name={formikFieldNames.postalCode}
            required={required && requiredFields.postalCode && isDomestic}
            showCheck
            type="text"
            placeholder={isDomestic ? 'e.g. 94110' : 'e.g. H3A 0B9'}
            aria-label="Street Address, ZIP Code"
            autoComplete="postal-code"
          />
        </Label>
      </div>
    </div>
  );
};

export default FormikAddressInput;
